import "./style.css";
import axios from "axios";
import logo from "./logo_black.png";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";
import { Link } from "react-router-dom";
// Same origin, like every other screen: nginx proxies /api and /auth to the backend on both
// ports, and an absolute http:// URL is blocked as mixed content the moment the site answers
// on https (2026-09-02). See backendConfig.js.
import { BACKEND_URL } from './backendConfig';



function GoogleRegister() {
    const { t } = useTranslation();
    const [pending, setPending] = useState(null);
    const [values, setValues] = useState({
        username: "",
        firstName: "",
        lastName: "",
        password: "",
        confirmPassword: "",
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        const loadPendingRegistration = async () => {
            try {
                const response = await axios.get(`${BACKEND_URL}/auth/google/pending`, {
                    withCredentials: true,
                });
                const data = response.data || {};
                setPending(data);
                setValues((prev) => ({
                    ...prev,
                    firstName: data.firstName || "",
                    lastName: data.lastName || "",
                }));
            } catch (err) {
                // i18n.t, not the hook's t: this effect runs once and is not to re-run on a language switch.
                setError(i18n.t('xAuth.regSessionExpired'));
            } finally {
                setChecking(false);
            }
        };

        loadPendingRegistration();
    }, []);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setValues((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (error) {
            setError("");
        }
    };

    const validateForm = () => {
        if (!values.username || values.username.length < 3 || values.username.length > 50) {
            return t('xAuth.valUsername');
        }

        if (!values.firstName || values.firstName.length < 2 || values.firstName.length > 50) {
            return t('xAuth.valFirstName');
        }

        if (!values.lastName || values.lastName.length < 2 || values.lastName.length > 50) {
            return t('xAuth.valLastName');
        }

        if (!values.password || values.password.length < 8) {
            return t('xAuth.valPwLength');
        }

        if (!/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/.test(values.password)) {
            return t('xAuth.valPwMix');
        }

        if (values.password !== values.confirmPassword) {
            return t('xAuth.valPwMatch');
        }

        return "";
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError("");

        try {
            await axios.post(`${BACKEND_URL}/auth/google/register`, values, {
                withCredentials: true,
            });
            window.location.replace("/");
        } catch (err) {
            const message = err.response?.data?.error || t('xAuth.regFailed');
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <header className="login-topbar">
                <div className="login-topbar-brand">
                    <img src={logo} alt="Helinox" className="login-logo" />
                    <span>HEVN PORTAL</span>
                </div>
            </header>

            <main className="login-card google-register-card">
                <p className="login-eyebrow">{t('xAuth.googleVerified')}</p>
                <h1 className="login-title">{t('xAuth.createAccount')}</h1>
                {pending?.email && (
                    <p className="login-subtitle">
                        {pending.email}
                    </p>
                )}

                {checking ? (
                    <div className="login-message">{t('xAuth.checkingGoogle')}</div>
                ) : (
                    <form className="login-form" onSubmit={handleSubmit}>
                        {error && (
                            <div className="login-error-message">
                                {error}
                            </div>
                        )}

                        <div className="login-field-group">
                            <span className="login-field-label">{t('xAuth.username')}</span>
                            <input
                                type="text"
                                name="username"
                                value={values.username}
                                onChange={handleChange}
                                className="login-field"
                                placeholder={t('xAuth.username')}
                                required
                            />
                        </div>
                        <div className="login-field-group">
                            <span className="login-field-label">{t('xAuth.firstName')}</span>
                            <input
                                type="text"
                                name="firstName"
                                value={values.firstName}
                                onChange={handleChange}
                                className="login-field"
                                placeholder={t('xAuth.firstNamePh')}
                                required
                            />
                        </div>
                        <div className="login-field-group">
                            <span className="login-field-label">{t('xAuth.lastName')}</span>
                            <input
                                type="text"
                                name="lastName"
                                value={values.lastName}
                                onChange={handleChange}
                                className="login-field"
                                placeholder={t('xAuth.lastNamePh')}
                                required
                            />
                        </div>
                        <div className="login-field-group">
                            <span className="login-field-label">{t('xAuth.password')}</span>
                            <input
                                type="password"
                                name="password"
                                value={values.password}
                                onChange={handleChange}
                                className="login-field"
                                placeholder={t('xAuth.password')}
                                autoComplete="new-password"
                                required
                            />
                        </div>
                        <div className="login-field-group">
                            <span className="login-field-label">{t('xAuth.confirm')}</span>
                            <input
                                type="password"
                                name="confirmPassword"
                                value={values.confirmPassword}
                                onChange={handleChange}
                                className="login-field"
                                placeholder={t('xAuth.confirmPassword')}
                                autoComplete="new-password"
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            className="login-submit-button"
                            disabled={loading || Boolean(error && !pending)}
                        >
                            {loading ? t('xAuth.creating') : t('xAuth.createAccount')}
                        </button>

                        <p className="login-back-link">
                            <Link to="/login">{t('xAuth.backToSignIn')}</Link>
                        </p>
                    </form>
                )}
            </main>
        </div>
    );
}

export default GoogleRegister;
